import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { http } from '@/lib/api/client'

export type ConnectorRange = '7d' | '30d' | '90d'
export type CallOutcome = 'ok' | 'empty' | 'refused' | 'error'

/** One call an AI tool made to the connector. Never the person's prompts or the content saved. */
export interface ConnectorCall {
  id: number
  at: string
  app: string
  tool: string
  tool_label: string
  outcome: CallOutcome
  reason: string | null
  message: string | null
  duration_ms: number
  person: { name: string; email: string } | null
  workspace: { id: string; name: string } | null
  website: string | null
}

export interface ConnectorOverview {
  range: ConnectorRange
  totals: { calls: number; people: number; workspaces: number }
  apps: { app: string; calls: number; people: number }[]
  tools: { tool: string; label: string; calls: number; people: number; empty: number; refused: number }[]
  stuck: { reason: string | null; label: string; calls: number; people: number }[]
  funnel: { key: string; label: string; people: number }[]
  recent: ConnectorCall[]
  tracking_since: string | null
}

export interface CustomerConnector {
  calls_30d: number
  apps: string[]
  connected: boolean
  recent: ConnectorCall[]
}

export function useConnectorOverview(range: ConnectorRange) {
  return useQuery({
    queryKey: ['ai-connector', range],
    queryFn: async () => (await http.get<{ data: ConnectorOverview }>('/ai-connector', { range })).data,
    placeholderData: keepPreviousData,
  })
}

export function useCustomerConnector(workspaceId: string) {
  return useQuery({
    queryKey: ['customers', workspaceId, 'ai-connector'],
    queryFn: async () =>
      (await http.get<{ data: CustomerConnector }>(`/customers/${workspaceId}/ai-connector`)).data,
  })
}
